import { useContext, useEffect, useRef, useState } from 'react';
import isDev from '../development';
import { ServerConnectionContext } from './ServerConnection';

export type ImageUploadTarget = {
  kind: 'challenge' | 'journey';
  resourceType: 'challenge' | 'event' | 'organization';
  resourceId: string;
};

export function ImageUploadField(props: {
  form: { value: string; helpText?: string };
  target: ImageUploadTarget;
  onBusyChange: (busy: boolean) => void;
}) {
  const { connection } = useContext(ServerConnectionContext);
  const [url, setUrl] = useState(props.form.value);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [imageFailed, setImageFailed] = useState(false);
  const request = useRef<XMLHttpRequest>();

  useEffect(() => {
    setUrl(props.form.value);
    setImageFailed(false);
    setError('');
    setProgress(null);
    props.onBusyChange(false);
    return () => request.current?.abort();
    // Each form object represents a new editing session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.form]);

  const upload = (file: File) => {
    const auth = connection?.auth;
    const token = typeof auth === 'object' ? auth.token : undefined;
    if (!token) {
      setError('Please sign in again before uploading.');
      return;
    }
    if (
      !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
      file.size > 10 * 1024 * 1024
    ) {
      setError('Choose a JPEG, PNG or WebP image smaller than 10 MB.');
      return;
    }
    setError('');
    setProgress(0);
    props.onBusyChange(true);
    const xhr = new XMLHttpRequest();
    request.current = xhr;
    const query = new URLSearchParams(props.target);
    xhr.open(
      'POST',
      `${isDev() ? 'http://localhost:8080' : ''}/media/images?${query}`,
    );
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.timeout = 60000;
    xhr.upload.onprogress = event => {
      if (event.lengthComputable)
        setProgress(Math.round((event.loaded / event.total) * 100));
    };
    const finish = () => {
      setProgress(null);
      props.onBusyChange(false);
    };
    xhr.onload = () => {
      try {
        const result = JSON.parse(xhr.responseText);
        if (xhr.status < 200 || xhr.status >= 300 || !result.imageUrl) {
          throw new Error(result.message || 'Image upload failed.');
        }
        props.form.value = result.imageUrl;
        setUrl(result.imageUrl);
        setImageFailed(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Image upload failed.');
      }
      finish();
    };
    xhr.onerror = xhr.ontimeout = () => {
      setError(
        'Upload failed. Your previous image is unchanged. Please try again.',
      );
      finish();
    };
    xhr.onabort = finish;
    const body = new FormData();
    body.append('image', file);
    xhr.send(body);
  };

  return (
    <section style={{ marginBottom: 20 }} aria-label="Image">
      <div style={{ marginBottom: 8 }}>Image</div>
      <img
        src={url && !imageFailed ? url : '/image-placeholder.svg'}
        alt={
          url && !imageFailed
            ? 'Selected image preview'
            : 'CornellGO placeholder'
        }
        onError={() => setImageFailed(true)}
        style={{
          width: '100%',
          height: 180,
          objectFit: 'cover',
          borderRadius: 8,
          background: '#f5f0e7',
        }}
      />
      <label style={{ display: 'block', marginTop: 10 }}>
        {url ? 'Replace image' : 'Upload image'}
        <input
          aria-label="Upload image"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={progress !== null}
          style={{ display: 'block', marginTop: 6 }}
          onChange={event => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) upload(file);
          }}
        />
      </label>
      {url && (
        <button
          type="button"
          disabled={progress !== null}
          style={{ marginTop: 10 }}
          onClick={() => {
            props.form.value = '';
            setUrl('');
            setImageFailed(false);
            setError('');
          }}
        >
          {props.target.kind === 'journey'
            ? 'Use first challenge image'
            : 'Remove image'}
        </button>
      )}
      {progress !== null && (
        <div role="status">
          {progress < 100 ? `Uploading… ${progress}%` : 'Preparing image…'}
        </div>
      )}
      {error && (
        <div role="alert" style={{ color: '#ad2424', marginTop: 8 }}>
          {error}
        </div>
      )}
      <p style={{ fontSize: 13, marginBottom: 0 }}>
        {props.form.helpText ||
          'JPEG, PNG or WebP · up to 10 MB. Save this form to apply the image.'}
      </p>
    </section>
  );
}
